import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";

const inputCls = "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none transition-colors focus:border-orange";
const btnCls = "flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white shadow-lg shadow-orange/30 transition-transform active:scale-95 disabled:opacity-60";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token");
    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center bg-lightbg px-6 py-10">
            <Logo size={46} />
            {token ? <NewPasswordForm token={token} /> : <RequestForm />}
            <Link to="/login" data-testid="back-to-login-link" className="mt-6 text-center text-sm font-semibold text-navy/50">← Back to login</Link>
        </div>
    );
}

function RequestForm() {
    const [identifier, setIdentifier] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() }); setMsg(data.message); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <>
            <h1 className="mt-6 font-heading text-3xl font-extrabold text-navy">Forgot password?</h1>
            <p className="mt-1 text-sm text-navy/55">Enter your email or phone. We'll email you a secure link to set a new password.</p>
            {msg ? (
                <div data-testid="forgot-sent-msg" className="mt-6 flex gap-3 rounded-2xl border border-teal/30 bg-teal/5 p-4 text-sm text-navy"><MailCheck className="shrink-0 text-teal" size={20} /> {msg}</div>
            ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <input data-testid="forgot-identifier-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="Email or 98xxxxxxxx" className={inputCls} required />
                    <button data-testid="forgot-submit-btn" disabled={busy} className={btnCls}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} SEND RESET LINK</button>
                </form>
            )}
        </>
    );
}

function NewPasswordForm({ token }) {
    const nav = useNavigate();
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 6) return toast.error("Use at least 6 characters");
        if (pw !== pw2) return toast.error("Passwords don't match");
        setBusy(true);
        try { const { data } = await api.post("/auth/reset-password", { token, new_password: pw }); toast.success(data.message); nav("/login"); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <>
            <h1 className="mt-6 font-heading text-3xl font-extrabold text-navy">Set a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
                <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className={inputCls} required />
                <input data-testid="reset-password-confirm-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className={inputCls} required />
                <button data-testid="reset-submit-btn" disabled={busy} className={btnCls}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} UPDATE PASSWORD</button>
            </form>
        </>
    );
}
